import type { BookingPaymentSession } from '@secretsoverflow/types';
import { loadScript } from '../lib/loadScript';
import { captureException } from '../lib/sentry';

/**
 * The card form on the last step of a booking.
 *
 * LiteAPI is the merchant of record. The payment intent lives on their Stripe
 * account and reaches us only as a client secret, and their SDK
 * (payment-wrapper.liteapi.travel) is meant to draw Stripe's form for it. It
 * cannot be trusted with the Pay button. It draws the button working before
 * Stripe's form is ready, and a press in that gap makes Stripe throw "We could
 * not retrieve data from the specified Element", which the SDK does not catch.
 * It has already set its own in-flight flag by then and never clears it, so
 * every later press does nothing: the guest is left on a form that looks fine
 * and cannot pay (Sentry SECRETSOVERFLOW_FRONTEND-B, a guest in Facebook's
 * in-app browser pressing Pay 1.9s in). The SDK has no ready callback, so this
 * cannot be fixed from outside it.
 *
 * So for their Stripe provider we draw the form ourselves, from exactly the
 * pieces their SDK uses: their config endpoint for the publishable key, the
 * same Stripe API version, the same element options, and the same markup and
 * class names (the site's CSS styles .lp-submit-button). Only the behaviour
 * around the button changes. It waits for Stripe's ready event, and a payment
 * that is refused or throws says so and leaves the button usable.
 *
 * Any other answer from their config goes back to their SDK, unchanged, and is
 * reported. A change on their side can then cost us this fix, never the
 * ability to take a payment.
 */

const VENDOR_SDK_URL = 'https://payment-wrapper.liteapi.travel/dist/liteAPIPayment.js?v=a1';
const VENDOR_CONFIG_URL = 'https://payment-wrapper.liteapi.travel/config';
const STRIPE_JS_URL = 'https://js.stripe.com/v3';

// What their config names for its Stripe form (liteAPIPaymentStripe.js), and
// the API version and element options that form uses, copied as they are so
// the payment is confirmed exactly as their SDK would confirm it.
const STRIPE_PROVIDER = 'LiteAPIPaymentProviderStripe';
const STRIPE_API_VERSION = '2023-10-16';
const CARD_OPTIONS = {
  layout: { type: 'accordion', defaultCollapsed: false, radios: true, spacedAccordionItems: false },
};
const WALLET_OPTIONS = {
  buttonType: { applePay: 'buy', googlePay: 'buy', paypal: 'buynow' },
  wallets: { applePay: 'always', googlePay: 'always', paypal: 'auto' },
  layout: 'auto',
  buttonTheme: { applePay: 'black' },
  buttonHeight: 55,
};

export const NOT_SENT = 'We could not send that payment. Please check your details and press Pay again.';
export const NOT_LOADED =
  'The payment form did not load. Please go back to your results and choose the room again.';

/** Thrown when the form cannot be drawn at all. Its message is for the guest. */
export class PaymentFormError extends Error {}

// The parts of Stripe.js this form uses.
interface StripeError {
  type?: string;
  code?: string;
  message?: string;
}
interface StripeElement {
  mount(host: HTMLElement): void;
  on(event: string, handler: (event: { error?: StripeError }) => void): void;
}
interface StripeElements {
  create(type: 'payment' | 'expressCheckout', options: object): StripeElement;
  submit(): Promise<{ error?: StripeError }>;
}
interface Stripe {
  elements(options: { clientSecret: string }): StripeElements;
  confirmPayment(options: {
    elements: StripeElements;
    confirmParams: { return_url: string };
  }): Promise<{ error?: StripeError }>;
}
type StripeFactory = (publishableKey: string, options: { apiVersion: string }) => Stripe;

interface LiteApiPaymentCtor {
  new (config: Record<string, unknown>): { handlePayment: () => void };
}

interface VendorConfig {
  provider?: { name?: string; origin?: string; className?: string };
  publicKey?: string;
}

/**
 * Draw the card form for a held room into `target`. Resolves once the form is
 * on the page; the payment itself happens later, from the form's own button.
 * On success Stripe sends the browser to the booking page with the booking id,
 * and the page finishes by asking our API to book it.
 */
export async function mountPaymentForm(session: BookingPaymentSession, target: string): Promise<void> {
  const returnUrl = `${window.location.origin}/book?booking=${encodeURIComponent(session.bookingId)}`;
  try {
    const config = await vendorConfig(session.environment);
    if (!isTheirStripe(config)) {
      captureException(new Error('LiteAPI named a payment provider we do not draw; using their SDK'), {
        provider: config.provider ?? null,
      });
      await mountVendorSdk(session, returnUrl, target);
      return;
    }
    drawStripeForm(await loadStripe(config.publicKey), session.clientSecret, returnUrl, target);
  } catch (err) {
    captureException(err, { area: 'payment-form', stage: 'load' });
    throw new PaymentFormError(NOT_LOADED);
  }
}

async function vendorConfig(environment: string): Promise<VendorConfig> {
  const res = await fetch(VENDOR_CONFIG_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ publicKey: environment }),
  });
  if (!res.ok) throw new Error(`LiteAPI payment config answered ${res.status}`);
  return (await res.json()) as VendorConfig;
}

function isTheirStripe(config: VendorConfig): config is VendorConfig & { publicKey: string } {
  return (
    config.provider?.origin === 'stripe' &&
    config.provider.className === STRIPE_PROVIDER &&
    typeof config.publicKey === 'string' &&
    config.publicKey.startsWith('pk_')
  );
}

async function loadStripe(publishableKey: string): Promise<Stripe> {
  const w = window as unknown as { Stripe?: StripeFactory };
  if (!w.Stripe) await loadScript(STRIPE_JS_URL);
  if (!w.Stripe) throw new Error('Stripe.js loaded without defining Stripe');
  return w.Stripe(publishableKey, { apiVersion: STRIPE_API_VERSION });
}

function drawStripeForm(stripe: Stripe, clientSecret: string, returnUrl: string, target: string): void {
  const host = document.querySelector<HTMLElement>(target);
  if (!host) throw new Error(`payment target ${target} is not on the page`);

  // Their markup, piece for piece: wallets, card form, button, message.
  const form = document.createElement('form');
  const walletSlot = document.createElement('div');
  const cardSlot = document.createElement('div');
  const button = document.createElement('button');
  button.type = 'submit';
  button.className = 'lp-submit-button';
  button.textContent = 'Pay';
  // Until Stripe says the card form is ready: pressed any sooner, Stripe throws
  // instead of paying.
  button.disabled = true;
  const buttonWrap = document.createElement('div');
  buttonWrap.className = 'lp-submit-button-wrapper';
  buttonWrap.append(button);
  const message = document.createElement('div');
  message.id = 'st-error-message';
  message.setAttribute('role', 'alert');
  form.append(walletSlot, cardSlot, buttonWrap, message);
  host.append(form);

  const elements = stripe.elements({ clientSecret });
  let ready = false;
  let paying = false;

  const settle = (): void => {
    button.disabled = !ready || paying;
    button.textContent = paying ? 'Processing…' : 'Pay';
  };

  // A payment that goes through leaves the page for the return URL, so an
  // attempt that comes back at all is one that did not go through.
  const pay = async (stage: string, attempt: () => Promise<StripeError | undefined>): Promise<void> => {
    paying = true;
    message.textContent = '';
    settle();
    try {
      const error = await attempt();
      if (error) message.textContent = error.message || NOT_SENT;
    } catch (err) {
      message.textContent = NOT_SENT;
      captureException(err, { area: 'payment-form', stage });
    }
    paying = false;
    settle();
  };

  const confirm = async (): Promise<StripeError | undefined> =>
    (await stripe.confirmPayment({ elements, confirmParams: { return_url: returnUrl } })).error;

  const card = elements.create('payment', CARD_OPTIONS);
  card.on('ready', () => {
    ready = true;
    settle();
  });
  card.on('loaderror', (event) => {
    message.textContent = NOT_LOADED;
    captureException(new Error('Stripe could not load the card form'), {
      area: 'payment-form',
      type: event.error?.type,
      code: event.error?.code,
    });
  });
  card.mount(cardSlot);

  // A wallet only offers itself once it is ready, so there is nothing to wait
  // for here, but its payment can fail or throw like any other.
  const wallets = elements.create('expressCheckout', WALLET_OPTIONS);
  wallets.on('confirm', () => {
    void pay('wallet', async () => {
      const { error } = await elements.submit();
      return error ?? confirm();
    });
  });
  wallets.mount(walletSlot);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (!ready || paying) return;
    void pay('card', confirm);
  });
}

/** Their SDK as it was, for a provider we do not draw ourselves. */
async function mountVendorSdk(session: BookingPaymentSession, returnUrl: string, target: string): Promise<void> {
  const w = window as unknown as { LiteAPIPayment?: LiteApiPaymentCtor };
  if (!w.LiteAPIPayment) await loadScript(VENDOR_SDK_URL);
  if (!w.LiteAPIPayment) throw new Error('The payment form could not be loaded.');
  new w.LiteAPIPayment({
    publicKey: session.environment,
    secretKey: session.clientSecret,
    returnUrl,
    targetElement: target,
    appearance: { theme: 'flat' },
    options: { business: { name: 'Secrets Hideaway' } },
  }).handlePayment();
}
